import React, {useEffect, useState} from 'react';
import {Button} from '../formElements/button';
import {ButtonGroupContainer, LoadingSpinner} from '../Layout.styles';
import {Markdown} from '../markdown';
import Input from '../formElements/input';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import * as yup from 'yup';
import {yupResolver} from '@hookform/resolvers/yup';
import {CloseButton, FormContainer, ModalContent, ModalHeader, ModalOverlay,} from './OTPModal.styles';

interface OTPModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (otpCode: string) => Promise<void>;
    onCancel: () => void;
    title: string;
    subtitle: string;
    validityText: string;
    loading: boolean;
    error: string;
}

const validationSchema = yup.object().shape({
    otpCode: yup.string()
        .required('OTP code is required')
});

export const OTPModal: React.FC<OTPModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    onCancel,
    title,
    subtitle,
    validityText,
    loading,
    error,
}) => {
    const [localError, setLocalError] = useState('');

    const methods = useForm({
        resolver: yupResolver(validationSchema) as any,
        defaultValues: {
            otpCode: '',
        },
    });

    useEffect(() => {
        if (!isOpen) {
            setLocalError('');
            if (methods.reset) {
                methods.reset();
            }
            return;
        }
    }, [isOpen, methods]);

    const handleSubmit = async (formData: any) => {
        try {
            setLocalError('');
            await onSubmit(formData.otpCode);
            // Success - modal will be closed by parent component
        } catch (error) {
            setLocalError('Invalid OTP code. Please try again.');
            if (methods.reset) {
                methods.reset();
            }
        }
    };

    const handleCancel = () => {
        setLocalError('');
        if (methods.reset) {
            methods.reset();
        }
        onCancel();
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay isOpen={isOpen}>
            <ModalContent>
                <ModalHeader>
                    <h2>{title}</h2>
                    <CloseButton onClick={onClose}>&times;</CloseButton>
                </ModalHeader>

                <Markdown children={subtitle}/>
                <Markdown children={validityText} className="subText"/>
                <FormProvider {...(methods as any)}>
                    <FormContainer onSubmit={methods.handleSubmit(handleSubmit)}>
                        <Controller
                            name="otpCode"
                            control={methods.control}
                            defaultValue=""
                            render={({field, fieldState}: any) => (
                                <Input
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    name={field.name}
                                    type="text"
                                    label="Enter OTP Code*"
                                    fieldName="otpCode"
                                    validation={fieldState?.error?.message || ''}
                                    maxLength={8}
                                    width="100%"
                                />
                            )}
                        />

                        {(localError || error) && (
                            <Markdown
                                children={localError || error}
                                className="validationText"
                            />
                        )}

                        <ButtonGroupContainer align="center">
                            <Button type="submit" className="centerLeftBtn">
                                {loading ? <LoadingSpinner show/> : 'Activate Card'}
                            </Button>
                            <Button onClick={handleCancel} type="button" className="centerRightBtn">
                                Cancel
                            </Button>
                        </ButtonGroupContainer>
                    </FormContainer>
                </FormProvider>

            </ModalContent>
        </ModalOverlay>
    );
};